/**
 * Where the page is actually visible, published once for everything that has
 * to lay itself out against it.
 *
 * A phone has two viewports. The **layout viewport** is the box CSS is laid
 * out against — `position: fixed`, `100dvh`, `env(safe-area-inset-*)` and
 * `window.innerHeight` all measure it. The **visual viewport** is the part of
 * that box the user can see, and it is exposed separately as
 * `window.visualViewport`. A software keyboard is what makes them disagree,
 * and on WebKit it shrinks only the second, so nothing in CSS notices: an
 * element at `bottom: 0` stays glued to the bottom of a box whose lower third
 * is now behind the keyboard.
 *
 * So the difference is measured here and written as three custom properties on
 * `<html>`, the same way `app-shell.tsx` publishes `--app-nav-width` for a
 * `fixed` element that cannot see the row it belongs to:
 *
 * - `--keyboard-inset` — layout-viewport pixels hidden below the visible box,
 *   which is exactly the number a `bottom:` needs to sit on the keyboard's
 *   top edge;
 * - `--visual-viewport-top` and `--visual-viewport-height` — where the visible
 *   box is and how tall it is, for a sheet that must cap itself at what is on
 *   screen.
 *
 * Subtracting `offsetTop` from the inset is not optional: when Safari has
 * scrolled the visual viewport down to reveal a field, the strip hidden below
 * it is smaller than the height difference by exactly that offset.
 *
 * Nothing is written where the API is absent — a desktop browser, jsdom — so
 * every `var(--x, fallback)` in the app falls back to the value it had before
 * any of this existed. The two hooks read the same store the properties are
 * written from, so a React consumer and the stylesheet can never be looking at
 * two different numbers.
 *
 * Installed once from `main.tsx`. Nothing else in the app reads
 * `visualViewport`.
 */

import { useSyncExternalStore } from 'react';

/** Layout-viewport px hidden below the visual viewport before the pin/sheet treat the keyboard as up. */
export const SOFTWARE_KEYBOARD_THRESHOLD_PX = 96;

let inset = 0;
const listeners = new Set<() => void>();

/** The live tracker's teardown, or `null` when nothing is installed. */
let installed: (() => void) | null = null;

const noop = (): void => undefined;

/**
 * Start watching the visual viewport. Returns the teardown.
 *
 * Idempotent: a second call while one is live changes nothing and hands back a
 * teardown that does nothing, so a stray installer cannot take the first one
 * down with it.
 */
export function installSoftwareKeyboardTracker(): () => void {
  // `null` per the DOM types, `undefined` on browsers without the API.
  const viewport = window.visualViewport;
  if (viewport === null || viewport === undefined) return noop;
  if (installed !== null) return noop;

  const sync = () => publish(viewport);

  const teardown = (): void => {
    if (installed !== teardown) return;
    installed = null;
    viewport.removeEventListener('resize', sync);
    viewport.removeEventListener('scroll', sync);
    window.removeEventListener('resize', sync);
    for (const property of PROPERTIES) document.documentElement.style.removeProperty(property);
    setInset(0);
  };

  installed = teardown;
  viewport.addEventListener('resize', sync);
  viewport.addEventListener('scroll', sync);
  // The window's own `resize` is for the other half of the fix: where
  // `interactive-widget=resizes-content` is honoured the *layout* viewport is
  // what moves, and that arrives here and not on the visual viewport.
  window.addEventListener('resize', sync);
  sync();

  return teardown;
}

const PROPERTIES = ['--keyboard-inset', '--visual-viewport-top', '--visual-viewport-height'];

function publish(viewport: VisualViewport): void {
  const top = Math.round(viewport.offsetTop);
  const height = Math.round(viewport.height);
  const hidden = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));

  const style = document.documentElement.style;
  style.setProperty('--keyboard-inset', `${hidden}px`);
  style.setProperty('--visual-viewport-top', `${top}px`);
  style.setProperty('--visual-viewport-height', `${height}px`);

  setInset(hidden);
}

function setInset(next: number): void {
  if (next === inset) return;
  inset = next;
  revealActiveField();
  for (const listener of listeners) listener();
}

/**
 * Nudge whatever is being typed into back into view.
 *
 * Moving the sheet is not sufficient on its own: it shrinks from the bottom
 * while its `scrollTop` stays where it was, so a field that was low in the old
 * visible range can end up just under the new bottom edge of a sheet that is
 * itself perfectly clear of the keyboard. `nearest` is what makes this safe —
 * it does nothing when the element is already fully visible, and scrolls only
 * the nearest scrollable ancestor when it is not.
 *
 * Text fields only. A focused button is not a caret, and chasing it would
 * scroll a page that nobody asked to move.
 */
function revealActiveField(): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return;
  const isField =
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement ||
    active.isContentEditable;
  if (!isField) return;
  active.scrollIntoView({ block: 'nearest' });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function keyboardInset(): number {
  return inset;
}

function keyboardOpen(): boolean {
  return inset > SOFTWARE_KEYBOARD_THRESHOLD_PX;
}

/** True when the software keyboard has stolen a meaningful slice of the viewport. */
export function useSoftwareKeyboard(): boolean {
  return useSyncExternalStore(subscribe, keyboardOpen);
}

/** The hidden strip in pixels, for a component that needs the number itself. */
export function useKeyboardInset(): number {
  return useSyncExternalStore(subscribe, keyboardInset);
}
